import main from '@/views/main/index.vue'

const RouteView = {
  name: 'RouteView',
  render: h => h('router-view')
}

const ALWAYS_ALLOWED_AUTH_ROUTE_NAMES = ['SystemProfile', 'SystemExportList']

const ROUTE_PERM_ALIASES = {
  SystemDepts: ['sys:dept', 'sys:dept:list'],
  SystemRoles: ['sys:role', 'sys:role:list'],
  userList: ['sys:user', 'sys:user:list'],
  SystemMenus: ['sys:menu', 'sys:menu:list'],
  SystemOperLogs: ['sys:operlog'],
  SystemLoginLogs: ['sys:logininfor']
}

const BACKEND_URL_ROUTE_ALIASES = {
  '/system/dept/list': ['SystemDepts'],
  '/system/role/list': ['SystemRoles'],
  '/system/user/list': ['userList'],
  '/system/menu': ['SystemMenus'],
  '/monitor/operlog': ['SystemOperLogs'],
  '/monitor/logininfor': ['SystemLoginLogs']
}

function normalizeText(value) {
  return String(value || '').trim()
}

function normalizeCode(value) {
  return normalizeText(value).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
}

function normalizePath(value) {
  const text = normalizeText(value).split('?')[0].split('#')[0]
  if (!text || text === '#') return ''
  const path = text.charAt(0) === '/' ? text : `/${text}`
  return path.replace(/\/+/g, '/').replace(/\/$/, '') || '/'
}

function toArray(value) {
  return Array.isArray(value) ? value : []
}

function joinPath(parentPath, path) {
  if (!path) return parentPath || ''
  if (path.charAt(0) === '/') return normalizePath(path)
  return normalizePath(`${parentPath || ''}/${path}`)
}

function relativePath(parentPath, fullPath) {
  const parent = normalizePath(parentPath)
  const full = normalizePath(fullPath)
  if (parent && full.indexOf(`${parent}/`) === 0) {
    return full.slice(parent.length + 1)
  }
  return full.charAt(0) === '/' ? full.slice(1) : full
}

function sortMenuList(list) {
  return toArray(list).slice().sort((a, b) => {
    const orderA = Number(a.orderNum || 0)
    const orderB = Number(b.orderNum || 0)
    if (orderA !== orderB) return orderA - orderB
    return Number(a.menuId || 0) - Number(b.menuId || 0)
  })
}

function createEmptyAccess(loaded) {
  return {
    loaded: !!loaded,
    routeNames: [],
    routeOrders: {},
    routeMenus: {},
    buttonPerms: []
  }
}

function addAccessRoute(access, route, menu, order) {
  if (!route || !route.name) return
  if (access.routeNames.indexOf(route.name) === -1) {
    access.routeNames.push(route.name)
  }
  access.routeOrders[route.name] = order
  access.routeMenus[route.name] = {
    title: menu.menuName,
    icon: normalizeText(menu.icon),
    menuId: menu.menuId,
    url: normalizePath(menu.url),
    perms: normalizeText(menu.perms)
  }
}

function addButtonPerms(menu, access) {
  if (!menu || menu.visible === '1') return
  if (normalizeText(menu.menuType).toUpperCase() === 'F' && menu.perms) {
    access.buttonPerms.push(menu.perms)
  }
  toArray(menu.children).forEach(child => addButtonPerms(child, access))
}

function flattenRoutes(routes, parentPath, result) {
  toArray(routes).forEach(route => {
    const fullPath = joinPath(parentPath, route.path)
    if (route.name) {
      result.byName[route.name] = route
      result.routeParentPaths[route.name] = normalizePath(parentPath)
    }
    if (route.meta) {
      if (route.meta.title) result.byTitle[route.meta.title] = route
      if (route.meta.code) result.byCode[normalizeCode(route.meta.code)] = route
    }
    if (fullPath) {
      result.byPath[normalizePath(fullPath).toLowerCase()] = route
      result.routePaths[route.name] = normalizePath(fullPath)
    }
    flattenRoutes(route.children, fullPath, result)
  })
}

function createStaticRouteMap(staticRoutes) {
  const map = {
    byName: Object.create(null),
    byPath: Object.create(null),
    byTitle: Object.create(null),
    byCode: Object.create(null),
    routePaths: Object.create(null),
    routeParentPaths: Object.create(null)
  }
  flattenRoutes(staticRoutes, '', map)
  return map
}

function findStaticRoute(menu, routeMap) {
  const url = normalizePath(menu.url).toLowerCase()
  const perms = normalizeText(menu.perms)
  const normalizedPerms = normalizeCode(perms)
  const aliasNames = BACKEND_URL_ROUTE_ALIASES[url] || []
  let i = -1

  while (++i < aliasNames.length) {
    if (routeMap.byName[aliasNames[i]]) return routeMap.byName[aliasNames[i]]
  }

  const routeNames = Object.keys(ROUTE_PERM_ALIASES)
  i = -1
  while (++i < routeNames.length) {
    const aliases = ROUTE_PERM_ALIASES[routeNames[i]]
    if (aliases.indexOf(perms) > -1 || aliases.map(normalizeCode).indexOf(normalizedPerms) > -1) {
      if (routeMap.byName[routeNames[i]]) return routeMap.byName[routeNames[i]]
    }
  }

  return routeMap.byPath[url] ||
    routeMap.byPath[url.replace(/s$/, '')] ||
    routeMap.byCode[normalizedPerms] ||
    routeMap.byTitle[normalizeText(menu.menuName)] ||
    null
}

function isDetailMenu(menu) {
  return normalizeText(menu && menu.menuName) === '详情'
}

function getDisplayMenu(menu) {
  const childMenus = sortMenuList(toArray(menu.children)).filter(item => normalizeText(item.menuType).toUpperCase() !== 'F')
  const menuType = normalizeText(menu.menuType).toUpperCase()
  if (menuType !== 'M' && childMenus.length === 1 && isDetailMenu(childMenus[0])) {
    return Object.assign({}, childMenus[0], {
      menuId: menu.menuId,
      parentId: menu.parentId,
      menuName: menu.menuName,
      icon: menu.icon,
      perms: menu.perms || childMenus[0].perms,
      orderNum: menu.orderNum,
      children: []
    })
  }
  return menu
}

function createDynamicRouteName(menu, staticRoute) {
  if (staticRoute && staticRoute.name) return staticRoute.name
  return `DynamicMenu${menu.menuId || normalizeCode(menu.menuName)}`
}

function createMenuMeta(menu, staticRoute, extra) {
  return Object.assign({}, staticRoute ? staticRoute.meta : {}, extra || {}, {
    title: normalizeText(menu.menuName),
    icon: normalizeText(menu.icon),
    code: normalizeText(menu.perms) || (staticRoute && staticRoute.meta && staticRoute.meta.code),
    requireAuth: true,
    hideInMenu: false,
    menuId: menu.menuId,
    backendUrl: normalizePath(menu.url)
  })
}

function createRouteFromMenu(menu, parentPath, routeMap, access, order) {
  if (!menu || menu.visible === '1') return null
  const menuType = normalizeText(menu.menuType).toUpperCase()
  if (menuType === 'F') {
    addButtonPerms(menu, access)
    return null
  }

  const displayMenu = getDisplayMenu(menu)
  const childrenMenus = displayMenu === menu
    ? sortMenuList(toArray(menu.children)).filter(item => normalizeText(item.menuType).toUpperCase() !== 'F')
    : []
  const staticRoute = findStaticRoute(displayMenu, routeMap) || findStaticRoute(menu, routeMap)
  const isContainer = childrenMenus.length > 0
  const staticPath = staticRoute && routeMap.routePaths[staticRoute.name] ? routeMap.routePaths[staticRoute.name] : ''
  const routeUrl = normalizePath(displayMenu.url) || staticPath || `/${normalizeCode(displayMenu.menuName) || `menu-${displayMenu.menuId}`}`
  const path = parentPath
    ? (routeUrl.indexOf(`${normalizePath(parentPath)}/`) === 0 ? relativePath(parentPath, routeUrl) : routeUrl)
    : routeUrl
  const childRoutes = childrenMenus
    .map((child, index) => createRouteFromMenu(child, routeUrl || parentPath, routeMap, access, `${order}.${String(index + 1).padStart(4, '0')}`))
    .filter(Boolean)

  if (isContainer && !childRoutes.length && (!staticRoute || !staticRoute.component)) {
    return null
  }
  if (!isContainer && (!staticRoute || !staticRoute.component)) {
    return null
  }

  const route = {
    path,
    name: createDynamicRouteName(displayMenu, staticRoute),
    meta: createMenuMeta(Object.assign({}, displayMenu, {
      url: routeUrl
    }), staticRoute),
    component: isContainer ? (parentPath ? RouteView : main) : (staticRoute && staticRoute.component ? staticRoute.component : RouteView)
  }

  if (staticRoute && staticRoute.redirect) {
    route.redirect = staticRoute.redirect
  }

  addAccessRoute(access, route, Object.assign({}, displayMenu, {
    url: routeUrl
  }), order)

  if (childRoutes.length) {
    route.children = childRoutes
    const firstLeaf = findFirstLeafRoute(childRoutes)
    if (firstLeaf && firstLeaf.name && route.component === RouteView) {
      route.redirect = {
        name: firstLeaf.name
      }
    }
  }

  return route
}

function findFirstLeafRoute(routes) {
  let i = -1
  while (++i < toArray(routes).length) {
    const route = routes[i]
    if (route.children && route.children.length) {
      const child = findFirstLeafRoute(route.children)
      if (child) return child
    } else if (route.name) {
      return route
    }
  }
  return null
}

function addHiddenRouteToParents(routes, hiddenRoute, parentRouteName, parentPath, access) {
  let i = -1
  while (++i < toArray(routes).length) {
    const route = routes[i]
    const routePath = route.path && route.path.charAt(0) === '/' ? route.path : joinPath(parentPath, route.path)
    if (route.name === parentRouteName) {
      const children = route.children || []
      if (!children.some(item => item.name === hiddenRoute.name)) {
        const hiddenPath = hiddenRoute.path && hiddenRoute.path.charAt(0) === '/'
          ? relativePath(routePath, hiddenRoute.path)
          : hiddenRoute.path
        children.push(Object.assign({}, hiddenRoute, {
          path: hiddenPath
        }))
        route.children = children
      }
      if (access.routeNames.indexOf(hiddenRoute.name) === -1) {
        access.routeNames.push(hiddenRoute.name)
      }
      access.routeOrders[hiddenRoute.name] = access.routeOrders[parentRouteName] || ''
      access.routeMenus[hiddenRoute.name] = access.routeMenus[parentRouteName] || {}
      return true
    }
    if (addHiddenRouteToParents(route.children, hiddenRoute, parentRouteName, routePath, access)) return true
  }
  return false
}

function addHiddenRouteToPath(routes, hiddenRoute, targetParentPath, parentPath, access) {
  let i = -1
  const targetPath = normalizePath(targetParentPath)
  while (++i < toArray(routes).length) {
    const route = routes[i]
    const routePath = route.path && route.path.charAt(0) === '/' ? route.path : joinPath(parentPath, route.path)
    if (normalizePath(routePath) === targetPath) {
      const children = route.children || []
      if (!children.some(item => item.name === hiddenRoute.name)) {
        const hiddenPath = hiddenRoute.path && hiddenRoute.path.charAt(0) === '/'
          ? relativePath(routePath, hiddenRoute.path)
          : hiddenRoute.path
        children.push(Object.assign({}, hiddenRoute, {
          path: hiddenPath
        }))
        route.children = children
      }
      if (access.routeNames.indexOf(hiddenRoute.name) === -1) {
        access.routeNames.push(hiddenRoute.name)
      }
      access.routeOrders[hiddenRoute.name] = access.routeOrders[route.name] || ''
      access.routeMenus[hiddenRoute.name] = access.routeMenus[route.name] || {}
      return true
    }
    if (addHiddenRouteToPath(route.children, hiddenRoute, targetParentPath, routePath, access)) return true
  }
  return false
}

function appendHiddenRoutes(dynamicRoutes, routeMap, access) {
  Object.keys(routeMap.byName).forEach(name => {
    const route = routeMap.byName[name]
    const meta = route.meta || {}
    const activeMenu = meta.activeMenu
    const isAlwaysAllowed = ALWAYS_ALLOWED_AUTH_ROUTE_NAMES.indexOf(name) > -1
    if (!meta.hideInMenu && !isAlwaysAllowed) return

    const parentName = activeMenu || 'System'
    const routePath = routeMap.routePaths[name]
    const hiddenRoute = routePath ? Object.assign({}, route, { path: routePath }) : route
    const staticParentPath = routeMap.routeParentPaths[name]
    const added = staticParentPath
      ? addHiddenRouteToPath(dynamicRoutes, hiddenRoute, staticParentPath, '', access)
      : addHiddenRouteToParents(dynamicRoutes, hiddenRoute, parentName, '', access)
    if (!added) {
      if (staticParentPath && routePath && normalizePath(staticParentPath) !== normalizePath(routePath)) {
        const parentPath = normalizePath(staticParentPath)
        const parentName = `Hidden${normalizeCode(parentPath)}Layout`
        let parentRoute = dynamicRoutes.find(item => normalizePath(item.path) === parentPath)
        if (!parentRoute) {
          parentRoute = {
            path: parentPath,
            name: parentName,
            component: main,
            meta: {
              title: route.meta && route.meta.title,
              requireAuth: true,
              hideInMenu: true
            },
            children: []
          }
          dynamicRoutes.push(parentRoute)
        }
        parentRoute.children = parentRoute.children || []
        if (!parentRoute.children.some(item => item.name === hiddenRoute.name)) {
          parentRoute.children.push(Object.assign({}, hiddenRoute, {
            path: relativePath(parentPath, routePath)
          }))
        }
        if (access.routeNames.indexOf(route.name) === -1) {
          access.routeNames.push(route.name)
        }
        access.routeOrders[route.name] = '9999'
        access.routeMenus[route.name] = {
          title: route.meta && route.meta.title,
          icon: route.meta && route.meta.icon
        }
        return
      }
      dynamicRoutes.push(Object.assign({}, hiddenRoute, {
        path: hiddenRoute.path && hiddenRoute.path.charAt(0) === '/' ? hiddenRoute.path : `/${hiddenRoute.path}`,
        meta: Object.assign({}, route.meta, {
          hideInMenu: true
        })
      }))
      if (access.routeNames.indexOf(route.name) === -1) {
        access.routeNames.push(route.name)
      }
      access.routeOrders[route.name] = '9999'
      access.routeMenus[route.name] = {
        title: route.meta && route.meta.title,
        icon: route.meta && route.meta.icon
      }
    }
  })
}

function cloneMenuRoute(route, parentPath) {
  const fullPath = route.path && route.path.charAt(0) === '/' ? normalizePath(route.path) : joinPath(parentPath, route.path)
  const cloned = {
    path: route.path,
    fullPath,
    name: route.name,
    meta: route.meta
  }
  if (route.redirect) cloned.redirect = route.redirect
  if (route.children && route.children.length) {
    cloned.children = route.children
      .filter(item => !(item.meta && item.meta.hideInMenu))
      .map(item => cloneMenuRoute(item, fullPath))
  }
  return cloned
}

export function buildDynamicRoutes(staticRoutes, backendTree) {
  const routeMap = createStaticRouteMap(staticRoutes)
  const access = createEmptyAccess(true)
  const dynamicRoutes = sortMenuList(backendTree)
    .map((menu, index) => {
      addButtonPerms(menu, access)
      return createRouteFromMenu(menu, '', routeMap, access, String(index + 1).padStart(4, '0'))
    })
    .filter(Boolean)

  appendHiddenRoutes(dynamicRoutes, routeMap, access)
  access.buttonPerms = access.buttonPerms.filter((item, index, list) => item && list.indexOf(item) === index)

  return {
    dynamicRoutes,
    menuList: dynamicRoutes
      .filter(route => !(route.meta && route.meta.hideInMenu))
      .map(route => cloneMenuRoute(route, '')),
    access
  }
}

export function createEmptyDynamicRouteState(loaded) {
  return {
    dynamicRoutes: [],
    menuList: [],
    access: createEmptyAccess(loaded),
    routeAdded: false
  }
}



// WEBPACK FOOTER //
// ./src/router/dynamicRoutes.js